import 'react-app-polyfill/ie9';
import 'react-app-polyfill/stable';

import React from "react";
import ReactDOM from "react-dom";
import {animated, useSpring, useTransition, config} from "react-spring";

import {ReactComponent as Logo} from "./img/logo.svg";

import {throttle} from 'lodash-es';

import mapImage from './img/map.png';

import challenge1Img from './img/icons/challenge1.png';
import challenge2Img from './img/icons/challenge2.png';
import donerenImg from './img/icons/doneren.png';
import kinderenImg from './img/icons/kinderen.png';
import leerlingenImg from './img/icons/leerlingen.png';
import leraarImg from './img/icons/leraar.png';
import nieuwsImg from './img/icons/nieuws.png';
import overheidImg from './img/icons/overheid.png';
import productieImg from './img/icons/productie.png';
import resultaat1Img from './img/icons/resultaat1.png';
import resultaat2Img from './img/icons/resultaat2.png';
import schoolImg from './img/icons/school.png';
import schoolSpullenImg from './img/icons/schoolspullen.png'

import {ReactComponent as Pointer} from './img/pointer.svg';
import {ReactComponent as Close} from './img/close.svg';
import {ReactComponent as Close2} from './img/close2.svg';
import {ReactComponent as Road} from './img/road.svg';
import {ReactComponent as Home} from './img/home.svg';
import {ReactComponent as Hand} from './img/hand.svg';
import {ReactComponent as Arrow} from './img/arrow.svg';

import {ReactComponent as Facebook} from './img/brands/facebook.svg';
import {ReactComponent as LinkedIn} from './img/brands/linkedin.svg';
import {ReactComponent as Twitter} from './img/brands/twitter.svg';

import wood from './img/wood.png';

import {useDimensions} from './dim';

import "./styles.scss";

/**
 * Use interval hook.
 *
 * @param callback
 * @param delay
 */
function useInterval(callback, delay) {
    const savedCallback = React.useRef();

    React.useEffect(() => {
        savedCallback.current = callback;
    });

    React.useEffect(() => {
        function tick() {
            savedCallback.current();
        }

        let id = setInterval(tick, delay);

        return () => clearInterval(id);
    }, [delay]);
}

/**
 * Hook that alerts clicks outside of the passed ref
 */
function useOutsideAlerter(ref, handler) {
    React.useEffect(() => {
        document.addEventListener("mousedown", handler);
        document.addEventListener("touchstart", handler);

        return () => {
            document.removeEventListener("mousedown", handler);
            document.removeEventListener("touchstart", handler);
        };
    });
}

function App() {
    /**
     * Get icon src.
     * @param id
     */
    function getIconSrc(id) {

        switch (id) {
            case 'challenge1':
                return challenge1Img;
            case 'challenge2':
                return challenge2Img;
            case 'doneren':
                return donerenImg;
            case 'kinderen':
                return kinderenImg;
            case 'leerlingen':
                return leerlingenImg;
            case 'leraar':
                return leraarImg;
            case 'nieuws':
                return nieuwsImg;
            case 'overheid':
                return overheidImg;
            case 'productie':
                return productieImg;
            case 'resultaat1':
                return resultaat1Img;
            case 'resultaat2':
                return resultaat2Img;
            case 'school':
                return schoolImg;
            case 'schoolSpullen':
                return schoolSpullenImg;
            default:
                return challenge1Img;
        }
    }

    const [preloading, setPreloading] = React.useState(true);

    const [loaded, setLoaded] = React.useState(false);

    const [items, setItems] = React.useState([]);

    const [item, setItem] = React.useState(-1);

    const [dialog, setDialog] = React.useState(false);

    const [help, setHelp] = React.useState(true);

    const [dragging, setDragging] = React.useState(false);

    const [position, setPosition] = React.useState({
        x: 0,
        y: 0,
    });

    const [map, setMap] = React.useState({
        position: {
            top: -400,
            left: -400,
        }
    });

    // Ref and dimensions.
    const [containerRef, containerDim] = useDimensions();
    const [imgRef, imgDim] = useDimensions();

    /**
     * Logo props.
     */
    const logoProps = useSpring({
        config: Object.assign({}, config.default, {
            duration: 300
        }),
        opacity: loaded ? 0 : 1,
    });

    /**
     * Map props.
     */
    const mapProps = useSpring({
        config: Object.assign({}, config.default, {
            duration: 300
        }),
        opacity: loaded ? 1 : 0,
    });

    const dialogTransition = useTransition(dialog, null, {
        unique: true,
        reset: true,
        from: {
            visibility: 'visible',
            opacity: 0,
        },
        enter: {
            opacity: 1,
        },
        leave: {
            visibility: 'none',
            opacity: 0,
        }
    });

    const dialogHelp = useTransition((help && !preloading), null, {
        unique: true,
        reset: true,
        from: {
            opacity: 0,
        },
        enter: {
            opacity: 1,
        },
        leave: {
            opacity: 0,
        }
    });

    /**
     * On Mouse down.
     * @type {Function}
     */
    const onMouesDown = React.useCallback((e) => {
        setDragging(true);

        const x = 'touches' in e ? e.touches[0].pageX : e.pageX;
        const y = 'touches' in e ? e.touches[0].pageY : e.pageY;

        // Safe position.
        setPosition({
            x,
            y
        });

    }, []);

    /**
     * Mouse up.
     * @type {Function}
     */
    const onMouseUp = React.useCallback((e) => {
        setDragging(false);
    }, []);

    /**
     * Set icon position.
     *
     * @param newPosition
     */
    const setIconPosition = React.useCallback((newPosition) => {
        const diffPos = {
            x: newPosition.x - position.x,
            y: newPosition.y - position.y,
        };

        // Save position.
        setPosition(diffPos);

        // Calculate center of screen.
        let top = (containerDim.height / 2) - (100 / 2) - newPosition.y;

        // Calculate center or left side of screen.
        let left = 0;
        if (window.innerWidth > 1024) {
            left = (containerDim.width / 6) - (100 / 2) - newPosition.x;
        } else {
            left = (containerDim.width / 2) - (100 / 2) - newPosition.x;
        }

        if (top > 0) {
            top = 0;
        } else if (top < (containerDim.height - imgDim.height)) {
            top = (containerDim.height - imgDim.height);
        }

        if (left > 0) {
            left = 0;
        } else if (left < (containerDim.width - imgDim.width)) {
            left = (containerDim.width - imgDim.width);
        }

        setMap({
            position: {
                left,
                top
            },
        });

    }, [position, imgDim, containerDim]);

    /**
     * Set new position.
     *
     * @param newPosition
     */
    const setNewPosition = React.useCallback((newPosition) => {
        const diff = {
            x: newPosition.x - position.x,
            y: newPosition.y - position.y,
        };

        // Save position.
        setPosition(newPosition);

        let top = (map.position.top + diff.y);
        let left = (map.position.left + diff.x);

        if (top > 0) {
            top = 0;
        } else if (top < (containerDim.height - imgDim.height)) {
            top = (containerDim.height - imgDim.height);
        }

        if (left > 0) {
            left = 0;
        } else if (left < (containerDim.width - imgDim.width)) {
            left = (containerDim.width - imgDim.width);
        }

        setMap({
            position: {
                top,
                left,
            }
        });
    }, [position, map, imgDim, containerDim]);

    /**
     * Mouse down.
     * @type {Function}
     */
    const onMouseMove = React.useCallback((e) => {
        if (!dragging) {
            return;
        }

        const x = 'touches' in e ? e.touches[0].pageX : e.pageX;
        const y = 'touches' in e ? e.touches[0].pageY : e.pageY;

        const newPosition = {
            x,
            y
        };

        setNewPosition(newPosition);

    }, [dragging, setNewPosition]);

    React.useLayoutEffect(() => {
        if (window.markers) {

            async function delayed() {
                await new Promise(resolve => setTimeout(resolve, 1000));

                // Set items.
                setItems(window.markers);

                // Set loaded.
                setLoaded(true);
            }

            delayed();
        }
    }, []);

    const refDialog = React.useRef(null);

    // Outside alerter.
    useOutsideAlerter(refDialog, (event) => {
        if (event.target && event.target.classList.contains('info')) {
            return;
        }

        if (refDialog.current && !refDialog.current.contains(event.target)) {
            setDialog(false);

            setItem(-1);
        }
    });

    useInterval(() => setPreloading(false), 2000);

    return (
        <div className="App">
            <header
                className='r-header'
                style={{
                    background: `url(${wood}`,
                }}
            >
                <div
                    className='info'
                    onClick={() => window.location.href = 'http://artefoundation.nl/'}
                >
                    <Home width='24px' height='24px'/> Sluit kaart
                </div>

                <div className='right'>
                    <span>Deel ons verhaal via</span>
                    <div className='share'>
                        <a href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`}
                           target='_blank' rel='noopener noreferrer'><Facebook width='20px' height='20px'/></a>
                        <a href={`https://twitter.com/intent/tweet?text=ARTE Foundation - Bekijk de reis van de Arte Foundation op ${window.location.href}.`}
                           target='_blank'
                           rel='noopener noreferrer'><Twitter width='20px' height='20px'/></a>
                        <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${window.location.href}`}
                           target='_blank' rel='noopener noreferrer'><LinkedIn width='20px' height='20px'/></a>
                    </div>
                </div>
            </header>

            <footer
                className='r-footer'
                style={{
                    background: `url(${wood}`,
                }}
            >
                {dialog ? (
                    <div
                        className='info'
                        onClick={() => {
                            if (preloading) {
                                return;
                            }

                            setDialog(false);
                            setItem(-1)
                        }}
                    >
                        <Close width='18px' height='18px'/> Sluit
                    </div>
                ) : (
                    <div
                        className='info'
                        onClick={() => {
                            if (preloading) {
                                return;
                            }

                            // Set help.
                            setHelp(false);

                            // Set item 0.
                            setItem(0);

                            // Set dialog.
                            setDialog(true);

                            // Set map position.
                            setIconPosition(items[0].icon.position);
                        }}
                    >
                        <Road width='37px' height='25px'/> Start je reis
                    </div>
                )}

                <div
                    className='right'
                >
                    &copy;2020. ARTE Responsible Stone Foundation.
                </div>
            </footer>

            <animated.div
                className="logo"
                style={logoProps}
            >
                <Logo width="250" height="250" alt="Arte Foundation Logo"/>
            </animated.div>

            {dialogHelp.map(({item, props, key}) => item && (
                <animated.div
                    key={key}
                    style={props}
                    className="dialog"
                >
                    <div className='help'>
                        <h2 className='title'><span>Road</span> to education</h2>
                        <p className='title-sub'>
                            Voordat alle kinderen in India naar school kunnen hebben we nog een lange weg te gaan.
                        </p>
                        <p className='tut'>
                            Begin nu je eigen reis door India, of volg het pad van onze foundation door op de
                            onderstaande button te klikken!
                        </p>
                        <div className='ani'>
                            <Arrow widht='16px' height={'16px'}/>
                            <Hand className='hand' width='45px' height='45px'/>
                        </div>
                        <div
                            className='close'
                            onClick={() => setHelp(false)}
                        >
                            Sluit <Close2 width={'16px'} height={'16px'}/>
                        </div>
                    </div>
                </animated.div>
            ))}

            {!preloading && dialogTransition.map(({item: res, props, key}) => res && items[item] ? (
                <animated.div
                    key={key}
                    ref={refDialog}
                    style={props}
                    className="dialog"
                >
                    <div className='item'>
                        <div className="thumbnail">
                            <img src={items[item].dialog.thumbnail} loading='lazy' alt={items[item].dialog.title}/>
                        </div>
                        <div className="box">
                            <h2 className='title'>{items[item].dialog.title}</h2>
                            <div className='date'>{items[item].dialog.date}</div>
                            <p className='content'>
                                {items[item].dialog.content}
                            </p>

                            <div className="tools">
                                <div className="left">
                                    <button
                                        className="prev"
                                        disabled={!dialog}
                                        onClick={async () => {
                                            setDialog(false);

                                            let newItem = item - 1;
                                            if (newItem < 0) {
                                                newItem = items.length - 1;
                                            }

                                            // Set map position.
                                            setIconPosition(items[newItem].icon.position);

                                            await new Promise(resolve => setTimeout(resolve, 500));

                                            // Set new item.
                                            setItem(newItem);

                                            await new Promise(resolve => setTimeout(resolve, 750));

                                            setDialog(true);
                                        }}>
                                        <Pointer width='16px' height='16px' style={{
                                            transform: 'rotate(-180deg)'
                                        }}/>
                                    </button>

                                    <button
                                        disabled={!dialog}
                                        onClick={async () => {
                                            setDialog(false);

                                            // Set map position.
                                            setIconPosition(items[0].icon.position);

                                            await new Promise(resolve => setTimeout(resolve, 500));

                                            // Set new item.
                                            setItem(0);

                                            await new Promise(resolve => setTimeout(resolve, 750));

                                            setDialog(true);
                                        }}>1
                                    </button>
                                </div>
                                <button className="current">{item + 1}/{items.length}</button>
                                <div className="right">
                                    <button
                                        disabled={!dialog}
                                        onClick={async () => {
                                            setDialog(false);

                                            const newItem = items.length - 1;

                                            // Set map position.
                                            setIconPosition(items[newItem].icon.position);

                                            await new Promise(resolve => setTimeout(resolve, 500));

                                            // Set new item.
                                            setItem(newItem);

                                            await new Promise(resolve => setTimeout(resolve, 750));

                                            setDialog(true);
                                        }}>{items.length}</button>

                                    <button
                                        disabled={!dialog}
                                        className="next"
                                        onClick={async () => {
                                            setDialog(false);

                                            let newItem = item + 1;
                                            if (newItem >= items.length) {
                                                newItem = 0;
                                            }

                                            // Set map position.
                                            setIconPosition(items[newItem].icon.position);

                                            await new Promise(resolve => setTimeout(resolve, 500));

                                            // Set new item.
                                            setItem(newItem);

                                            await new Promise(resolve => setTimeout(resolve, 750));

                                            setDialog(true);
                                        }}>
                                        <Pointer width='16px' height='16px'/>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </animated.div>
            ) : '')}

            <animated.div
                className={dragging ? 'map' : 'map transition'}
                style={Object.assign({}, mapProps, {
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                })}
            >
                <div
                    ref={containerRef}
                    className="background"
                    onMouseDown={throttle(onMouesDown, 10)}
                    onMouseUp={throttle(onMouseUp, 10)}
                    onMouseMove={throttle(onMouseMove, 10)}
                    onTouchStart={throttle(onMouesDown, 10)}
                    onTouchEnd={throttle(onMouseUp, 10)}
                    onTouchMove={throttle(onMouseMove, 10)}
                    style={{
                        maxWidth: `1920px`,
                        maxHeight: `1375px`,
                    }}
                >
                    {items.map(({icon, dialog}, index) => (
                        <div
                            key={index}
                            className={index === item ? 'icon active' : 'icon'}
                            style={{
                                top: icon.position.y + map.position.top,
                                left: icon.position.x + map.position.left,
                            }}
                            onClick={() => {
                                if (preloading) {
                                    return;
                                }

                                // Set help dialog false.
                                setHelp(false);

                                // Set item id.
                                setItem(index);

                                // Show dialog.
                                setDialog(true);

                                // Set map position.
                                setIconPosition(icon.position);
                            }}
                        >
                            <img src={getIconSrc(icon.icon)} loading='lazy' alt={dialog.title}/>
                        </div>
                    ))}
                    <img
                        src={mapImage}
                        ref={imgRef}
                        loading='eager'
                        style={{
                            width: 'calc(1920px + 500px)',
                            height: 'calc(1375px + 500px)',
                            top: `${map.position.top}px`,
                            left: `${map.position.left}px`
                        }}
                        alt="Arte Foundation Map"
                    />
                </div>
            </animated.div>
        </div>
    );
}

const rootElement = document.getElementById("root");
ReactDOM.render(<App/>, rootElement);
